/**
 * scope-baglami.tsx
 *
 * NE İŞE YARAR : Seçili kurumu tutar — hangi yurdun verisine bakıldığı
 * KAYNAK SPEC  : 15-arayuz-standardi.md §5.3 (scope seçici) · 04-api-sozlesmesi.md
 *                M1-01 (`GET /organizations`) · §2.3 (istemciden gelen `organization_id`)
 *                X-02 (`GET /me` — roller) · ADR-007 §2 (efektif scope)
 * BAĞLI OLDUĞU : src/oturum/baglam.tsx · src/api/liste.ts
 * KULLANAN     : src/kabuk/kabuk.tsx · src/kabuk/scope-secici.tsx · liste ekranları
 * DİKKAT       : 1) SEÇİM **YETKİ DEĞİL, FİLTREDİR** (`04` §2.3). İstemciden gelen
 *                   `organization_id` yetki kararında kullanılmaz; sunucu her istekte
 *                   efektif scope'u kendi hesaplar ve RLS uygular. Buradaki seçim yalnız
 *                   `M2-01`, `M5-01`, `M6-01` gibi uçların **filtre parametresini**
 *                   besler ve kullanıcıya nerede olduğunu gösterir. Seçimi değiştirmek
 *                   kimseye yeni bir satır açmaz.
 *                2) SEÇENEKLER `M1-01`DEN GELİR, `/me`DEN DEĞİL. `/me` yalnız **atama
 *                   noktalarını** döndürür; efektif scope o noktaların **alt ağacıdır**
 *                   (ADR-002 §3). Şube yöneticisi şubeye atanır ama altındaki yurtlarda
 *                   da çalışır — `/me`den kurulan bir liste o yurtları hiç göstermezdi.
 *                   `M1-01` ise RLS'ten geçer: dönen her satır zaten efektif scope'tadır.
 *                3) `organization.read` YOKSA `/me`YE DÜŞÜLÜR. `M1-01` bu izni istiyor
 *                   ve her rolde yok (`02` §5.1). İzinsiz rol için tek bilgi kaynağı
 *                   `/me`nin rol satırlarıdır; dar ama yanlış değil — kullanıcı en
 *                   azından atandığı kurumu görür.
 *                4) SEÇİM SAKLANIR AMA **DOĞRULANIR**. Tarayıcıda duran bir kurum
 *                   kimliği, yetki kaldırıldıktan sonra da orada kalır; her açılışta
 *                   listede olup olmadığına bakılır ve yoksa *"tümü"*ne düşülür.
 *                   Doğrulanmasaydı kullanıcı, artık göremediği bir kurumun adını üst
 *                   çubukta okumaya devam ederdi.
 *                5) VARSAYILAN *"TÜMÜ"*DÜR ve bu bir kolaylık değil, **doğru olan**dır:
 *                   filtresiz istek, sunucunun zaten uyguladığı efektif scope'u gösterir.
 *                   Rastgele bir kurum seçili başlasaydı, kullanıcı verisinin bir kısmını
 *                   sebepsiz göremezdi.
 *                6) KAPANMAKTA OLAN KURUM LİSTEDE KALIR, KAPANMIŞ OLAN KALMAZ.
 *                   `CLOSING` bir kurum hâlâ çalışıyor: öğrencisi, yerleşimi ve bekleyen
 *                   işi var (ADR-011). Listeden düşseydi, kapanış işlerini yürüten
 *                   kullanıcı tam da o kuruma filtreleyemezdi. `CLOSED` ve `ARCHIVED`
 *                   ise iş görmüyor; üstelik seçici bu yüzden yıllar geçtikçe uzardı.
 *                   Süzme **istemcide** yapılıyor çünkü `M1-01`in `status` filtresi
 *                   eşitliktir, dışlama değil (`04` §5).
 *                7) TEK KURUMU OLAN KULLANICI İÇİN SEÇİCİ **AÇILIR MENÜ DEĞİLDİR**
 *                   (`15` §5.3 — *"birden çok organizasyonda yetkisi olan kullanıcı
 *                   seçer"*). Seçenek sunulmayan bir açılır menü, olmayan bir kararı
 *                   varmış gibi gösterir.
 * SON GÜNCELLEME: 2026-08-31 13:05:00
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { listeyiCoz } from '../api/liste';
import { useOturum } from '../oturum/baglam';

/** `M1-01` yanıtındaki kurum satırı — alan politikası bazı alanları gizleyebilir. */
export interface Kurum {
  readonly id: string;
  readonly name?: string;
  readonly code?: string;
  readonly type?: string;
  readonly status?: string;
}

/** Seçicide görünen durumlar (başlıktaki not 6). */
const CALISAN_DURUMLAR: readonly string[] = ['ACTIVE', 'CLOSING'];

export interface ScopeBaglami {
  readonly kurumlar: readonly Kurum[];
  /** `null` = tümü — filtresiz (başlıktaki not 5). */
  readonly seciliId: string | null;
  readonly secili: Kurum | null;
  readonly sec: (id: string | null) => void;
  readonly yukleniyor: boolean;
}

const Baglam = createContext<ScopeBaglami | null>(null);

const DEPO_ANAHTARI = 'yurt.scope.secili';

function seciliOku(): string | null {
  try {
    const deger = localStorage.getItem(DEPO_ANAHTARI);
    return deger === null || deger === '' ? null : deger;
  } catch {
    // Depoya erişilemiyorsa (gizli mod, site verisi kapalı) seçim oturumluk kalır.
    return null;
  }
}

function seciliYaz(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(DEPO_ANAHTARI);
    } else {
      localStorage.setItem(DEPO_ANAHTARI, id);
    }
  } catch {
    // Yazılamadıysa seçim yalnız bu sekme boyunca yaşar.
  }
}

export function ScopeSaglayici({ children }: { children: ReactNode }): ReactElement {
  const { cagir, ben, izinVar } = useOturum();

  const [kurumlar, setKurumlar] = useState<readonly Kurum[]>([]);
  const [seciliId, setSeciliId] = useState<string | null>(seciliOku);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    let birakildi = false;

    async function yukle(): Promise<void> {
      // Başlıktaki not 3 — izin yoksa `/me`nin rol satırlarına düşülür.
      if (!izinVar('organization.read')) {
        const rollerden = new Map<string, Kurum>();
        for (const rol of ben?.roles ?? []) {
          rollerden.set(rol.organization_id, {
            id: rol.organization_id,
            name: rol.organization_name,
          });
        }
        if (!birakildi) {
          setKurumlar([...rollerden.values()]);
          setYukleniyor(false);
        }
        return;
      }

      try {
        const ham = await cagir<unknown>({ yol: '/organizations' });
        const liste = listeyiCoz<Kurum>(ham);
        if (!birakildi) {
          // Başlıktaki not 6 — `status` alan politikasıyla gizlenmiş olabilir; o zaman
          // satır **elenmez**. Bilinmeyen bir durumu yok saymak, kullanıcının
          // yetkili olduğu bir kurumu sebepsiz gizlemek olurdu.
          setKurumlar(
            liste.satirlar.filter(
              (kurum) => kurum.status === undefined || CALISAN_DURUMLAR.includes(kurum.status),
            ),
          );
        }
      } catch {
        // Kurum listesi çekilemezse kabuk yine de çizilir: scope seçici *"tümü"*de
        // kalır ve kullanıcı çalışmaya devam eder. Kabuğu bir liste isteğinin
        // başarısına bağlamak, tek bir arızada bütün arayüzü kapatırdı.
        if (!birakildi) {
          setKurumlar([]);
        }
      } finally {
        if (!birakildi) {
          setYukleniyor(false);
        }
      }
    }

    void yukle();
    return () => {
      birakildi = true;
    };
  }, [cagir, ben, izinVar]);

  // Başlıktaki not 4 — saklanan seçim listeye karşı doğrulanır.
  useEffect(() => {
    if (yukleniyor || seciliId === null) {
      return;
    }
    if (!kurumlar.some((kurum) => kurum.id === seciliId)) {
      // Saklanan seçim listeye karşı doğrulanıyor ve AYNI ANDA depodan siliniyor;
      // iki işlem ayrılamaz, o yüzden çizim sırasında türetilemez (68. adım).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSeciliId(null);
      seciliYaz(null);
    }
  }, [kurumlar, seciliId, yukleniyor]);

  const sec = useCallback((id: string | null): void => {
    setSeciliId(id);
    seciliYaz(id);
  }, []);

  const secili = useMemo(
    () => kurumlar.find((kurum) => kurum.id === seciliId) ?? null,
    [kurumlar, seciliId],
  );

  const deger = useMemo<ScopeBaglami>(
    () => ({ kurumlar, seciliId, secili, sec, yukleniyor }),
    [kurumlar, seciliId, secili, sec, yukleniyor],
  );

  return <Baglam.Provider value={deger}>{children}</Baglam.Provider>;
}

/**
 * Seçili kurumu okur.
 *
 * Sağlayıcı dışında çağrılırsa patlar — `null` dönseydi bir liste ekranı sessizce
 * filtresiz çalışır ve kullanıcı, seçtiği kurumun dışındaki satırları görürdü.
 */
export function useScope(): ScopeBaglami {
  const deger = useContext(Baglam);
  if (deger === null) {
    throw new Error('useScope, ScopeSaglayici dışında çağrıldı.');
  }
  return deger;
}
